import { Link, type LinkProps } from '@tanstack/react-router'
import { ArrowRight, ShieldAlert } from 'lucide-react'

import { PageHeader } from '@/components/page-header'
import { PermissionGate } from '@/components/permission-gate'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { Card } from '@/components/ui/card'
import { AdminCapabilityGaps } from '@/features/admin/capability-gaps'
import { ADMIN_MODULE_PERMISSIONS, ADMIN_PERMISSIONS, useAdminPermissions } from '@/features/admin/permissions'
import type { PermissionCode } from '@/lib/permissions'

/**
 * Configuration is rarely urgent and permanently consequential, which is the whole reason it
 * waits for a desk: nobody needs to change a role's permissions from a bus, and a mis-set one is
 * discovered in a regulatory return months later.
 */
const CONFIGURATION_REASON =
  'A change here is rarely urgent and permanently consequential — a user, a role, or a branch grant is set once and read by every request afterwards.'

interface AdminCardDefinition {
  title: string
  description: string
  to: LinkProps['to']
  permission: PermissionCode
}

const ADMIN_CARDS: AdminCardDefinition[] = [
  {
    title: 'Users',
    description: 'The staff directory — status, roles, branch access, and two-factor state.',
    to: '/admin/users',
    permission: ADMIN_PERMISSIONS.userView,
  },
  {
    title: 'Roles',
    description: 'What each role grants, grouped by module, and who holds it.',
    to: '/admin/roles',
    permission: ADMIN_PERMISSIONS.roleView,
  },
  {
    title: 'Branches',
    description: 'The branches this session can operate in — not yet an institution-wide register.',
    to: '/admin/branches',
    permission: ADMIN_PERMISSIONS.branchView,
  },
]

export function AdminPage() {
  const permissions = useAdminPermissions()

  return (
    <RequiresWiderScreen screen="Administration" reason={CONFIGURATION_REASON}>
      <PageHeader
        title="Administration"
        description="Users and roles, branches, and what the Access module still cannot do from a screen."
      />

      {!permissions.canEnterModule ? (
        <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
          <ShieldAlert className="size-6 text-warning-text" aria-hidden />
          <p className="text-sm font-medium text-heading">Administration is restricted</p>
          <p className="max-w-lg text-xs text-muted">
            Nothing in this module is open to your role. Its screens are gated on{' '}
            {ADMIN_MODULE_PERMISSIONS.map((code, index) => (
              <span key={code}>
                {index > 0 ? ', ' : ''}
                <span className="font-mono">{code}</span>
              </span>
            ))}{' '}
            and their siblings, and the server checks each of them again on every request.
          </p>
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {ADMIN_CARDS.map((card) => (
            <PermissionGate key={card.to} permission={card.permission}>
              <Link to={card.to} className="block">
                <Card className="flex h-full flex-col gap-1.5 p-4 transition-colors hover:border-border-strong hover:bg-surface-hover">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-heading">{card.title}</p>
                    <ArrowRight className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
                  </div>
                  <p className="text-xs text-body">{card.description}</p>
                </Card>
              </Link>
            </PermissionGate>
          ))}
        </div>
      )}

      <AdminCapabilityGaps />
    </RequiresWiderScreen>
  )
}
